import React from 'react';
import { Highlight } from '@mantine/core';

interface HighlightMatch {
  text: string;
  highlight: boolean;
}

/**
 * Advanced highlighting utility that matches the backend search logic
 * Supports multiple search strategies:
 * 1. All words matching
 * 2. Partial word matching (2+ words)
 * 3. Significant words (4+ characters)
 * 4. Prefix matching
 * 5. Fuzzy character matching
 */
export const getAdvancedHighlightMatches = (
  text: string,
  query: string
): HighlightMatch[] => {
  if (!query || !text) {
    return [{ text, highlight: false }];
  }

  const queryWords = query.trim().split(/\s+/).filter(word => word.length > 0);
  if (queryWords.length === 0) {
    return [{ text, highlight: false }];
  }

  // Create a set to track which parts of the text should be highlighted
  const highlightRanges: { start: number; end: number }[] = [];
  
  // Strategy 1: Find exact word matches (case insensitive)
  queryWords.forEach(word => {
    const regex = new RegExp(`\\b${escapeRegex(word)}\\b`, 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
      highlightRanges.push({
        start: match.index,
        end: match.index + match[0].length
      });
    }
  });

  // Strategy 2: Find partial word matches (prefix matching)
  queryWords.forEach(word => {
    const regex = new RegExp(`\\b${escapeRegex(word)}`, 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
      highlightRanges.push({
        start: match.index,
        end: match.index + match[0].length
      });
    }
  });

  // Strategy 3: Find character-level matches for significant words (4+ chars)
  const significantWords = queryWords.filter(word => word.length >= 4);
  significantWords.forEach(word => {
    const regex = new RegExp(escapeRegex(word), 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
      highlightRanges.push({
        start: match.index,
        end: match.index + match[0].length
      });
    }
  });

  // Strategy 4: Individual character matching for all queries
  const allChars = query.toLowerCase().replace(/\s+/g, '').split('');
  allChars.forEach(char => {
    const regex = new RegExp(escapeRegex(char), 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
      highlightRanges.push({
        start: match.index,
        end: match.index + 1
      });
    }
  });

  // Strategy 5: Fuzzy matching - find sequences of characters that appear in order
  queryWords.forEach(word => {
    if (word.length >= 3) { // Only apply fuzzy matching for words 3+ chars
      const fuzzyRanges = findFuzzyMatches(text, word);
      highlightRanges.push(...fuzzyRanges);
    }
  });

  // Strategy 6: For the full query, find fuzzy matches
  const cleanQuery = query.replace(/\s+/g, '');
  if (cleanQuery.length >= 3) {
    const fullQueryFuzzyRanges = findFuzzyMatches(text, cleanQuery);
    highlightRanges.push(...fullQueryFuzzyRanges);
  }

  // Strategy 7: Sequential character matching for better fuzzy support
  queryWords.forEach(word => {
    if (word.length >= 2) {
      const sequentialRanges = findSequentialCharacterMatches(text, word);
      highlightRanges.push(...sequentialRanges);
    }
  });

  // Merge overlapping ranges
  const mergedRanges = mergeOverlappingRanges(highlightRanges);
  
  // Convert ranges to highlight matches
  return createHighlightMatches(text, mergedRanges);
};

/**
 * Create highlight array from text and ranges
 */
const createHighlightMatches = (
  text: string,
  ranges: { start: number; end: number }[]
): HighlightMatch[] => {
  if (ranges.length === 0) {
    return [{ text, highlight: false }];
  }

  const matches: HighlightMatch[] = [];
  let currentIndex = 0;

  ranges.forEach(range => {
    // Add non-highlighted text before this range
    if (currentIndex < range.start) {
      matches.push({
        text: text.substring(currentIndex, range.start),
        highlight: false
      });
    }

    // Add highlighted text
    matches.push({
      text: text.substring(range.start, range.end),
      highlight: true
    });

    currentIndex = range.end;
  });

  // Add remaining non-highlighted text
  if (currentIndex < text.length) {
    matches.push({
      text: text.substring(currentIndex),
      highlight: false
    });
  }

  return matches;
};

/**
 * Merge overlapping or adjacent highlight ranges
 */
const mergeOverlappingRanges = (
  ranges: { start: number; end: number }[]
): { start: number; end: number }[] => {
  if (ranges.length === 0) return [];

  // Sort ranges by start position
  const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [sortedRanges[0]];

  for (let i = 1; i < sortedRanges.length; i++) {
    const current = sortedRanges[i];
    const lastMerged = merged[merged.length - 1];

    // If ranges overlap or are adjacent, merge them
    if (current.start <= lastMerged.end + 1) {
      lastMerged.end = Math.max(lastMerged.end, current.end);
    } else {
      merged.push(current);
    }
  }

  return merged;
};

/**
 * Find fuzzy matches - characters that appear in order but not necessarily consecutive
 * This supports cases like "Terive" matching "Thrive" where T-r-i-v-e appear in order
 */
const findFuzzyMatches = (
  text: string,
  query: string
): { start: number; end: number }[] => {
  if (!query || !text) return [];
  
  const ranges: { start: number; end: number }[] = [];
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  let queryIndex = 0;
  let matchStart = -1;
  
  for (let textIndex = 0; textIndex < textLower.length && queryIndex < queryLower.length; textIndex++) {
    if (textLower[textIndex] === queryLower[queryIndex]) {
      if (matchStart === -1) {
        matchStart = textIndex;
      }
      queryIndex++;
      
      // If we've matched all characters in the query
      if (queryIndex === queryLower.length) {
        ranges.push({
          start: matchStart,
          end: textIndex + 1
        });
        
        // Reset for potential additional matches
        queryIndex = 0;
        matchStart = -1;
      }
    } else if (matchStart !== -1) {
      // If we were in the middle of a match but hit a non-matching character
      // Check if we should continue the fuzzy match or restart
      const remainingQuery = queryLower.slice(queryIndex);
      const remainingText = textLower.slice(textIndex);
      
      // If the current character appears later in the remaining query, 
      // we might want to restart from here
      if (remainingQuery.includes(textLower[textIndex])) {
        // Continue with current match
        continue;
      } else if (queryIndex > queryLower.length * 0.5) {
        // If we've matched more than half, consider it a partial match
        ranges.push({
          start: matchStart,
          end: textIndex
        });
      }
      
      // Reset and check if current character starts a new match
      queryIndex = 0;
      matchStart = -1;
      if (textLower[textIndex] === queryLower[0]) {
        matchStart = textIndex;
        queryIndex = 1;
      }
    }
  }
  
  // Handle case where we reached end of text with a partial match
  if (matchStart !== -1 && queryIndex > queryLower.length * 0.3) {
    ranges.push({
      start: matchStart,
      end: text.length
    });
  }
  
  return ranges;
};

/**
 * Enhanced fuzzy matching that finds individual characters in sequence
 * This highlights each matching character even if they're not consecutive
 */
const findSequentialCharacterMatches = (
  text: string,
  query: string
): { start: number; end: number }[] => {
  if (!query || !text) return [];
  
  const ranges: { start: number; end: number }[] = [];
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  let queryIndex = 0;
  
  for (let textIndex = 0; textIndex < textLower.length && queryIndex < queryLower.length; textIndex++) {
    if (textLower[textIndex] === queryLower[queryIndex]) {
      ranges.push({
        start: textIndex,
        end: textIndex + 1
      });
      queryIndex++;
    }
  }
  
  return ranges;
};

/**
 * Escape special regex characters
 */
const escapeRegex = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Advanced Highlight component that uses the improved matching logic
 */
export const AdvancedHighlight: React.FC<{
  children: string;
  highlight: string;
  highlightStyles?: React.CSSProperties;
}> = ({ children, highlight, highlightStyles }) => {
  const matches = getAdvancedHighlightMatches(children, highlight);
  
  return (
    <>
      {matches.map((match, index) => (
        <span
          key={index}
          style={match.highlight ? { 
            backgroundColor: '#fff3cd', 
            fontWeight: 500,
            ...highlightStyles 
          } : undefined}
        >
          {match.text}
        </span>
      ))}
    </>
  );
};

/**
 * Get highlight terms for Mantine's Highlight component
 * This creates an array of terms that should be highlighted
 */
export const getHighlightTerms = (query: string): string[] => {
  if (!query) return [];
  
  const queryWords = query.trim().split(/\s+/).filter(word => word.length > 0);
  const terms: string[] = [];
  
  // Add individual words
  terms.push(...queryWords);
  
  // Add significant words (4+ characters) for partial matching
  const significantWords = queryWords.filter(word => word.length >= 4);
  terms.push(...significantWords);
  
  // For short queries, add individual characters
  if (query.length <= 3) {
    terms.push(...query.toLowerCase().split(''));
  }
  
  // Remove duplicates
  return [...new Set(terms)];
};
